/**
 * DashboardPage — RC 1.20 (RC 1.33.C legacy-credit sweep)
 *
 * Honest command centre. No fake Career Score, no fabricated
 * application/pending counts. Only renders counts we actually
 * have, and falls back to clean empty states with CTAs otherwise.
 *
 * Pulls:
 *   - Usage snapshot (from /api/account/plan — RC 1.32/1.33 single
 *     source of truth; the legacy /api/credits wallet is no longer
 *     read here)
 *   - Recent jobs (last 5 by created_at) via /api/jobs
 *   - Recent application packs (last 5) via /api/application-packs
 *   - Recent CV variants + cover letters via the respective
 *     /api/cv-variants and /api/cover-letters lists
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useFreeFitLimit } from '@/hooks/useFreeFitLimit';
import { apiFetch } from '@/lib/api';
import { cachedFetch, peek, KEYS } from '@/lib/listCache';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DashboardPreferencesNudge from '@/components/DashboardPreferencesNudge';
import HelpOrientationCard from '@/components/HelpOrientationCard';
import FirstRunMentalModelDialog, {
  isFirstRunDismissed,
} from '@/components/FirstRunMentalModelDialog';
import {
  Briefcase, FileText, Mail, Send, Plus, Gauge,
} from 'lucide-react';

function dt(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-GB', {day:'numeric', month:'short'}); }
  catch (_) { return ''; }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { greetingName } = useProfile();
  const { limit: freeFitLimit } = useFreeFitLimit();
  const [jobs, setJobs]       = useState([]);
  const [packs, setPacks]     = useState([]);
  const [variants, setVariants] = useState([]);
  const [letters, setLetters] = useState([]);
  const [plan, setPlan]       = useState(null);
  // RC 1.49.x reviewer-trust polish — first-run mental-model
  // splash. Opens automatically on first arrival when the user
  // hasn't dismissed it. The window event "ndor:first-run-open"
  // (fired by the AppLayout user-menu "Show first-run guide"
  // entry) lets the user re-open it on demand from any page —
  // we listen for it here while the dashboard is mounted, and
  // also expose a re-opener on the dashboard itself via the
  // same listener for symmetry.
  const [firstRunOpen, setFirstRunOpen] = useState(false);
  // RC 1.49.4 Tier 1 — first-paint hydration from the dashboard
  // bundle cache. If a fresh entry exists from a recent visit, we
  // skip the skeleton entirely.
  const [loading, setLoading] = useState(() => {
    const seed = peek(KEYS.dashboardBundle(), 180_000);
    return !(seed.hit && seed.fresh);
  });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Cache the entire 5-call bundle under a single key. The
        // dashboard renders sliced top-5 lists from each list, so
        // serving stale-while-revalidate is safe — a fresh fetch
        // happens in the background and silently updates state.
        const bundle = await cachedFetch(
          KEYS.dashboardBundle(),
          async () => {
            const [j, p, v, l, pl] = await Promise.all([
              apiFetch('/api/jobs').catch(() => []),
              apiFetch('/api/application-packs').catch(() => []),
              apiFetch('/api/cv-variants').catch(() => []),
              apiFetch('/api/cover-letters').catch(() => []),
              apiFetch('/api/account/plan').catch(() => null),
            ]);
            return { j, p, v, l, pl };
          },
          { ttlMs: 180_000 },
        );
        if (!live || !bundle) return;
        const { j, p, v, l, pl } = bundle;
        // /api/jobs and /api/application-packs may be envelopes or
        // bare arrays — normalise before slicing.
        const jArr = Array.isArray(j) ? j : (j?.items || []);
        const pArr = Array.isArray(p) ? p : (p?.items || []);
        setJobs(jArr.slice(0, 5));
        setPacks(pArr.slice(0, 5));
        setVariants(Array.isArray(v) ? v.slice(0, 5) : []);
        setLetters(Array.isArray(l) ? l.slice(0, 5) : []);
        setPlan(pl);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  // RC 1.49.x — open first-run splash on initial dashboard mount
  // when the user has not previously dismissed it. Listens for
  // `ndor:first-run-open` so the user-menu re-open works no matter
  // which page fires it (the dashboard re-renders via its mount).
  useEffect(() => {
    if (!user?.id) return;
    if (!isFirstRunDismissed(user.id)) {
      setFirstRunOpen(true);
    }
    const handler = () => setFirstRunOpen(true);
    window.addEventListener('ndor:first-run-open', handler);
    return () => window.removeEventListener('ndor:first-run-open', handler);
  }, [user?.id]);

  // RC 1.33.C — derive usage card values from /api/account/plan.
  // Base (or no-entitlement) users see a "Base / N free analyses per
  // day" card where N is the runtime-configured free-tier limit
  // (RC 1.38.A — pulled from `app_settings.limits.free_daily_analyze_fit`
  // via the `useFreeFitLimit` hook). Focus / Flow users see their
  // included balance clamped to zero, plus the extra balance when
  // non-zero.
  const usageCard = (() => {
    const code = plan?.plan_code || 'base';
    const isPaid = code === 'focus' || code === 'flow';
    if (!isPaid) {
      return {
        value: 'Base',
        helper: `${freeFitLimit} free analyses per day`,
        testid: 'dashboard-stat-usage-base',
      };
    }
    const included = Math.max(0, plan?.included_usage_remaining || 0);
    const extra    = Math.max(0, plan?.extra_usage_remaining    || 0);
    // RC 1.49.x.WF — operator directive 2026-05-06: card shows the
    // TOTAL credits remaining as the big number, with the
    // included / extra split moved into the helper line. Previously
    // the big number rendered `included` only (0 in the operator's
    // case) which was misleading when extra Credits had been topped up.
    const total = included + extra;
    return {
      value: total.toLocaleString(),
      helper: extra > 0
        ? `${included} included + ${extra} extra`
        : 'Credits remaining',
      testid: 'dashboard-stat-usage-paid',
    };
  })();

  return (
    <div data-testid="dashboard-page" className="space-y-6 sm:space-y-8">
      {/* RC 1.49.x reviewer-trust polish — first-run mental model. */}
      <FirstRunMentalModelDialog
        open={firstRunOpen}
        onOpenChange={setFirstRunOpen}
        userId={user?.id}
      />
      {/* ── RC 1.21 premium hero header ── */}
      <section
        className="brand-hex-grid rounded-2xl border border-ndor-edge px-6 sm:px-8 py-7 sm:py-9 relative"
        data-testid="dashboard-hero"
      >
        <div className="relative z-[1] flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">
              Command Centre
            </p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink" data-testid="dashboard-greeting">
              {greetingName ? <>Hello, <span className="text-gold-accent">{greetingName}</span></> : 'Welcome back'}
            </h1>
            <p className="mt-1.5 text-sm text-ndor-ink-secondary max-w-xl">
              Manage your jobs, documents, and applications from one place.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/jobs"><Button variant="outline" size="sm" className="border-ndor-edge bg-ndor-surface-alt hover:bg-ndor-surface text-ndor-ink" data-testid="dashboard-add-job-button"><Plus className="h-4 w-4 mr-1"/> Add job</Button></Link>
            {/* RC 1.41.H0 — legacy "Generate CV" hero CTA removed.
                CV generation must happen exclusively inside the Job
                Application Wizard (Jobs → Job Detail → Apply → Step 2). */}
          </div>
        </div>
      </section>

      {/* RC 1.43.C.1 — Optional preference nudge. Auto-suppresses
          when ≥1 preference is set or the user explicitly dismisses. */}
      <DashboardPreferencesNudge />

      {/* Real, honest summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-stat-usage">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Credits</p>
              <p
                className="text-2xl font-serif-display font-semibold text-ndor-ink mt-1"
                data-testid={usageCard.testid}
              >
                {loading ? '—' : usageCard.value}
              </p>
              <p className="text-[11px] text-ndor-ink-muted mt-0.5 font-manrope">
                {loading ? '' : usageCard.helper}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-ndor-info-light text-ndor-info flex items-center justify-center border border-ndor-edge">
              <Gauge className="h-5 w-5"/>
            </div>
          </CardContent>
        </Card>
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-stat-jobs">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Tracked jobs</p>
              <p className="text-2xl font-serif-display font-semibold text-ndor-ink mt-1">{loading ? '—' : jobs.length}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-ndor-success-light text-ndor-success flex items-center justify-center border border-ndor-edge">
              <Briefcase className="h-5 w-5"/>
            </div>
          </CardContent>
        </Card>
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-stat-packs">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Application packs</p>
              <p className="text-2xl font-serif-display font-semibold text-ndor-ink mt-1">{loading ? '—' : packs.length}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-ndor-warning-light text-ndor-warning flex items-center justify-center border border-ndor-edge">
              <Send className="h-5 w-5"/>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent jobs */}
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-recent-jobs">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-serif-display text-ndor-ink flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-ndor-gold"/> Recent jobs
            </CardTitle>
            <Link to="/jobs" className="text-xs text-ndor-ink-muted hover:text-ndor-ink">See all →</Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading ? (
              <p className="text-sm text-ndor-ink-muted">Loading…</p>
            ) : jobs.length === 0 ? (
              <div className="py-6 text-center space-y-2">
                <p className="text-sm text-ndor-ink-muted">No jobs yet.</p>
                <Link to="/jobs"><Button variant="outline" size="sm" className="border-ndor-edge bg-ndor-surface-alt">Add a job</Button></Link>
              </div>
            ) : (
              jobs.map(j => (
                <Link
                  key={j.id} to={`/jobs/${j.id}`}
                  className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-ndor-surface-alt transition-colors"
                  data-testid={`dashboard-job-${j.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ndor-ink truncate">{j.title || 'Untitled'}</p>
                    <p className="text-xs text-ndor-ink-muted truncate">{j.company || '—'}</p>
                  </div>
                  <p className="text-xs text-ndor-ink-muted whitespace-nowrap ml-3 font-mono-ui">{dt(j.created_at)}</p>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent application packs */}
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-recent-packs">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-serif-display text-ndor-ink flex items-center gap-2">
              <Send className="h-4 w-4 text-ndor-gold"/> Recent application packs
            </CardTitle>
            <Link to="/applications" className="text-xs text-ndor-ink-muted hover:text-ndor-ink">See all →</Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading ? (
              <p className="text-sm text-ndor-ink-muted">Loading…</p>
            ) : packs.length === 0 ? (
              <div className="py-6 text-center space-y-2">
                <p className="text-sm text-ndor-ink-muted">No application packs yet.</p>
                <Link to="/applications"><Button variant="outline" size="sm" className="border-ndor-edge bg-ndor-surface-alt">Start a pack</Button></Link>
              </div>
            ) : (
              packs.map(p => (
                <Link
                  key={p.id} to={`/applications/${p.id}`}
                  className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-ndor-surface-alt transition-colors"
                  data-testid={`dashboard-pack-${p.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ndor-ink truncate">Pack #{(p.id || '').slice(0, 8)}</p>
                    <p className="text-xs text-ndor-ink-muted truncate">Status: {p.pack_status || '—'}</p>
                  </div>
                  <p className="text-xs text-ndor-ink-muted whitespace-nowrap ml-3 font-mono-ui">{dt(p.created_at)}</p>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent CV variants */}
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-recent-variants">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-serif-display text-ndor-ink flex items-center gap-2">
              <FileText className="h-4 w-4 text-ndor-gold"/> Recent CV variants
            </CardTitle>
            <Link to="/documents" className="text-xs text-ndor-ink-muted hover:text-ndor-ink" data-testid="dashboard-recent-variants-open">Open library →</Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading ? (
              <p className="text-sm text-ndor-ink-muted">Loading…</p>
            ) : variants.length === 0 ? (
              <div className="py-6 text-center space-y-2">
                <p className="text-sm text-ndor-ink-muted">No CV variants generated yet.</p>
                {/* RC 1.41.H0 — direct empty-state users to the Jobs
                    page; tailored CVs are generated inside the
                    application wizard, not here. */}
                <Link to="/jobs"><Button variant="outline" size="sm" className="border-ndor-edge bg-ndor-surface-alt" data-testid="dashboard-recent-variants-empty-cta">Browse jobs to start →</Button></Link>
              </div>
            ) : (
              variants.map(v => (
                <div
                  key={v.id}
                  className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-ndor-surface-alt transition-colors"
                  data-testid={`dashboard-variant-${v.id}`}
                >
                  <p className="text-sm text-ndor-ink truncate">{v.variant_name || v.role_family || 'Variant'}</p>
                  <p className="text-xs text-ndor-ink-muted whitespace-nowrap ml-3 font-mono-ui">{dt(v.created_at)}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent cover letters */}
        <Card className="border-ndor-edge bg-ndor-surface brand-card-hover" data-testid="dashboard-recent-letters">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-serif-display text-ndor-ink flex items-center gap-2">
              <Mail className="h-4 w-4 text-ndor-gold"/> Recent cover letters
            </CardTitle>
            <Link to="/documents?tab=letters" className="text-xs text-ndor-ink-muted hover:text-ndor-ink">Open →</Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading ? (
              <p className="text-sm text-ndor-ink-muted">Loading…</p>
            ) : letters.length === 0 ? (
              <div className="py-6 text-center space-y-2">
                <p className="text-sm text-ndor-ink-muted">No cover letters yet.</p>
                <Link to="/documents?tab=letters"><Button variant="outline" size="sm" className="border-ndor-edge bg-ndor-surface-alt">Generate a cover letter</Button></Link>
              </div>
            ) : (
              letters.map(l => (
                <div
                  key={l.id}
                  className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-ndor-surface-alt transition-colors"
                  data-testid={`dashboard-letter-${l.id}`}
                >
                  <p className="text-sm text-ndor-ink truncate">{l.title || l.target_role || 'Cover letter'}</p>
                  <p className="text-xs text-ndor-ink-muted whitespace-nowrap ml-3 font-mono-ui">{dt(l.created_at)}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* RC 1.63.1 — Help & orientation stub. Calm, persistent rail
          so freshly-authenticated candidates always have one click
          to How It Works + the Candidate User Guide. */}
      <HelpOrientationCard workspace="candidate" />
    </div>
  );
}
